'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

interface Props {
  callbackURL?: string
}

/**
 * Form di accesso: le credenziali vengono verificate dal server contro le API
 * Nextcloud (OCS). Nessun redirect alla UI di Nextcloud.
 */
export function NextcloudLoginForm({ callbackURL = '/portal/admin' }: Props) {
  const router = useRouter()
  const [loginName, setLoginName] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/auth/sign-in/nextcloud', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ loginName: loginName.trim(), password }),
      })
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as {
          message?: string
          error?: string
        }
        setError(data.message || data.error || 'Autenticazione non riuscita')
        setLoading(false)
        return
      }
      router.push(callbackURL)
      router.refresh()
    } catch {
      setError('Impossibile contattare il servizio di autenticazione. Riprova.')
      setLoading(false)
    }
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[color:var(--league-muted)]">
          Email o nome utente
        </span>
        <input
          type="text"
          name="loginName"
          autoComplete="username"
          placeholder="nome@esempio.it oppure nome utente"
          required
          value={loginName}
          onChange={(e) => setLoginName(e.target.value)}
          className="min-h-11 rounded-md border border-[color:var(--league-line)] px-3 py-2 text-[16px] outline-none focus:border-[color:var(--league-brand)]"
        />
      </label>

      <label className="flex flex-col gap-1">
        <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[color:var(--league-muted)]">
          Password
        </span>
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="min-h-11 rounded-md border border-[color:var(--league-line)] px-3 py-2 text-[16px] outline-none focus:border-[color:var(--league-brand)]"
        />
      </label>

      {error && <p className="text-[14px] text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={loading}
        className="mt-1 inline-flex min-h-11 items-center justify-center rounded-md bg-[color:var(--league-brand-solid)] px-5 py-3 text-[14px] font-semibold text-white transition hover:opacity-90 disabled:opacity-60"
      >
        {loading ? 'Verifica in corso…' : 'Accedi'}
      </button>
    </form>
  )
}
